import { useEffect, useMemo, useRef, useState } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import ConfirmDialog from "components/admin/ConfirmDialog";
import { apiGet, apiPost } from "lib/apiClient";
import { useAuth } from "contexts/AuthContext";
import { KENYAN_BANKS } from "data/kenyanBanks";
import { FaShieldHalved, FaBuildingColumns, FaMobileScreenButton, FaHourglassHalf, FaCircleCheck, FaClockRotateLeft, FaTriangleExclamation } from "react-icons/fa6";

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  const remainingMs = Math.max(0, target - now);
  if (remainingMs <= 0) return null;
  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const fraction = 1 - remainingMs / (7 * 24 * 60 * 60 * 1000);
  return { days, hours, minutes, seconds, progressPct: Math.min(100, Math.max(0, fraction * 100)) };
}

export default function PayoutAccountPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("client-portal.manage");

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [accountType, setAccountType] = useState("BANK");
  const [bankName, setBankName] = useState(KENYAN_BANKS[0]);
  const [accountIdentifier, setAccountIdentifier] = useState("");
  const [accountName, setAccountName] = useState("");
  const [note, setNote] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const registeredAccount = useRef(null);

  function load() {
    setLoading(true);
    setError(null);
    apiGet("/portal/payout-account")
      .then((result) => setStatus(result))
      .catch((err) => setError(err.message || "Failed to load payout account."))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  const countdown = useCountdown(status?.nextEligibleChangeAt);

  const identifierLabel = accountType === "BANK" ? "Account number" : "M-Pesa / mobile money number";
  const identifierPlaceholder = accountType === "BANK" ? "e.g. 0123456789" : "e.g. 0712345678";

  const canSubmit = accountName.trim() && accountIdentifier.trim() && (accountType !== "BANK" || bankName);

  async function findRegisteredAccount() {
    const lastDigits = accountIdentifier.replace(/\D/g, "").slice(-4);
    if (!lastDigits) return null;
    const result = await apiGet("/portal/payout-account/external-accounts").catch(() => null);
    const matches = (result?.externalAccounts || []).filter((a) => a.maskedLabel?.replace(/\D/g, "").endsWith(lastDigits));
    return matches.find((a) => a.isActive) || matches[0] || null;
  }

  async function registerExternalAccount(fullAccountName) {
    const key = `${accountType}|${accountIdentifier.trim()}|${fullAccountName}`;
    if (registeredAccount.current?.key === key) return registeredAccount.current.id;
    let id;
    try {
      const account = await apiPost("/portal/payout-account/external-accounts", { accountType, accountIdentifier, accountName: fullAccountName });
      id = account.externalAccount.id;
    } catch (err) {
      if (err?.code !== "EXTERNAL_ACCOUNT_ALREADY_REGISTERED") throw err;
      const existing = await findRegisteredAccount();
      if (!existing) throw err;
      id = existing.id;
    }
    registeredAccount.current = { key, id };
    return id;
  }

  async function confirmSetPayoutAccount() {
    setBusy(true);
    setFormError(null);
    try {
      const fullAccountName = accountType === "BANK" ? `${bankName} — ${accountName}` : accountName;
      const externalAccountId = await registerExternalAccount(fullAccountName);
      await apiPost("/portal/payout-account/requests", { externalAccountId, note: note.trim() || null });
      registeredAccount.current = null;
      setConfirmOpen(false);
      setAccountIdentifier("");
      setAccountName("");
      setNote("");
      load();
    } catch (err) {
      setFormError(err.message || "Failed to submit the payout account change request.");
    } finally {
      setBusy(false);
    }
  }

  const input = "w-full rounded-md border border-border bg-transparent p-3 text-sm outline-none focus:border-ring focus:ring-[3px] focus:ring-ring/50 text-foreground placeholder:text-muted-foreground dark:bg-input/30";
  const label = "ml-1 text-sm font-medium text-navy-700 dark:text-white";

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;

  return (
    <div className="mx-auto max-w-3xl">
      <Card extra="p-6 mb-5 border-2 border-navy-700/10 dark:border-white/10">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-lightPrimary text-2xl text-brand-500 dark:bg-navy-700">
            <FaShieldHalved />
          </div>
          <div>
            <p className="text-lg font-semibold text-navy-700 dark:text-white">Payout account security</p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Every change to where your settlements are paid out is reviewed and approved by our compliance team before it takes
              effect — it never applies automatically. For your protection, an account can be changed at most once every 7 days.
            </p>
          </div>
        </div>
      </Card>

      {error && <p className="mb-4 text-sm font-medium text-red-500">{error}</p>}

      <Card extra="p-6 mb-5">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">Current payout account</p>
        {status?.profile ? (
          <div className="mt-3 rounded-xl border border-gray-100 p-4 dark:border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaCircleCheck className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm font-semibold text-navy-700 dark:text-white">{status.externalAccount?.maskedLabel || "Account on file"}</p>
                  <p className="text-xs text-gray-500">Settlements are sent here.</p>
                </div>
              </div>
              <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-600 dark:bg-green-400/10">Active</span>
            </div>
            {status.externalAccount && (
              <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-white/10">
                {status.externalAccount.verificationStatus === "VERIFIED" ? (
                  <>
                    <FaCircleCheck className="h-4 w-4 shrink-0 text-green-500" />
                    <p className="text-xs text-gray-500">
                      Number verified{status.externalAccount.verificationMethod === "OTP" ? " by SMS code" : status.externalAccount.verificationMethod === "MICRO_DEPOSIT" ? " by deposit" : ""}.
                    </p>
                  </>
                ) : (
                  <>
                    <FaTriangleExclamation className="h-4 w-4 shrink-0 text-amber-500" />
                    <p className="text-xs text-gray-500">
                      {status.externalAccount.otpEligible
                        ? "This number hasn't completed our separate SMS/deposit verification yet — every change is still reviewed by compliance before it takes effect."
                        : "Bank accounts are verified through compliance review rather than SMS or deposit."}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">No payout account has been set up yet.</p>
        )}
      </Card>

      {status?.pendingRequest ? (
        <Card extra="p-6">
          <div className="flex items-center gap-3">
            <FaHourglassHalf className="h-6 w-6 text-amber-500" />
            <div>
              <p className="text-lg font-semibold text-navy-700 dark:text-white">Change request pending review</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Submitted {new Date(status.pendingRequest.createdAt).toLocaleString()}. Our compliance team will approve or reject
                it — you'll see the update here once they do.
              </p>
            </div>
          </div>
        </Card>
      ) : !canManage ? (
        <Card extra="p-6">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Only your organization's primary contact can request a payout account change.
          </p>
        </Card>
      ) : countdown ? (
        <Card extra="p-6">
          <div className="flex items-center gap-3">
            <FaClockRotateLeft className="h-6 w-6 text-gray-400" />
            <p className="text-lg font-semibold text-navy-700 dark:text-white">Next change available in</p>
          </div>
          <div className="mt-4 grid grid-cols-4 gap-2 text-center">
            {[
              ["Days", countdown.days],
              ["Hours", countdown.hours],
              ["Min", countdown.minutes],
              ["Sec", countdown.seconds],
            ].map(([lbl, val]) => (
              <div key={lbl} className="rounded-xl bg-lightPrimary p-3 dark:bg-navy-700">
                <p className="num text-2xl font-semibold text-navy-700 dark:text-white">{String(val).padStart(2, "0")}</p>
                <p className="text-xs font-medium text-muted-foreground">{lbl}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${countdown.progressPct}%` }} />
          </div>
          <p className="mt-3 text-xs text-gray-500">To protect your funds, the payout account can only be changed once every 7 days.</p>
        </Card>
      ) : (
        <Card extra="p-6">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Request a payout account change</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">This becomes a pending request until compliance approves it.</p>

          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className={label}>Account type</label>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setAccountType("BANK")}
                  className={`flex items-center justify-center gap-2 rounded-sm border p-3 text-sm font-semibold transition-colors ${
                    accountType === "BANK" ? "border-brand-500 bg-lightPrimary text-brand-500 dark:bg-navy-700" : "border-gray-200 text-gray-500 dark:border-white/10"
                  }`}
                >
                  <FaBuildingColumns /> Bank
                </button>
                <button
                  type="button"
                  onClick={() => setAccountType("MOBILE_MONEY")}
                  className={`flex items-center justify-center gap-2 rounded-sm border p-3 text-sm font-semibold transition-colors ${
                    accountType === "MOBILE_MONEY" ? "border-brand-500 bg-lightPrimary text-brand-500 dark:bg-navy-700" : "border-gray-200 text-gray-500 dark:border-white/10"
                  }`}
                >
                  <FaMobileScreenButton /> Mobile money
                </button>
              </div>
            </div>

            {accountType === "BANK" && (
              <div className="md:col-span-2">
                <label className={label}>Bank</label>
                <select className={`${input} mt-2`} value={bankName} onChange={(e) => setBankName(e.target.value)}>
                  {KENYAN_BANKS.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={accountType === "BANK" ? "" : "md:col-span-2"}>
              <InputField label={identifierLabel} placeholder={identifierPlaceholder} value={accountIdentifier} onChange={(e) => setAccountIdentifier(e.target.value)} />
            </div>
            <InputField label="Account holder name" placeholder="As it appears on the account" value={accountName} onChange={(e) => setAccountName(e.target.value)} />
            <div className="md:col-span-2">
              <label className={label}>Note for compliance (optional)</label>
              <textarea className={`${input} mt-2`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why you're changing accounts…" />
            </div>
          </div>

          <button
            onClick={() => setConfirmOpen(true)}
            disabled={!canSubmit}
            className="mt-5 flex items-center gap-2 rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            <FaShieldHalved /> Submit for compliance review
          </button>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        icon={FaShieldHalved}
        title="Submit this payout account for review?"
        description="Your current payout account stays active until compliance approves this request. You won't be able to submit another change for 7 days after approval."
        confirmLabel="Submit for review"
        tone="primary"
        busy={busy}
        error={formError}
        onConfirm={confirmSetPayoutAccount}
        onCancel={() => {
          setConfirmOpen(false);
          setFormError(null);
        }}
      />
    </div>
  );
}
